<template>
  <InputWrapper
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    input-id="purchase-cargo-insurance-equipment-type"
    :label="t('carriers.equipmentTypes')"
    class="col-12"
    :errors="v$.equipmentType.id.$errors"
  >
    <RygenDropdown
      :id="inputId"
      v-model="equipmentType"
      v-bind="ariaProps"
      :class="inputClasses"
      :invalid="invalid"
      :options="availableEquipmentTypes.data"
      :loading="availableEquipmentTypes.loading"
      data-key="id"
      option-label="name"
      append-to="body"
      filter
      :placeholder="t('carriers.equipmentTypes')"
    />
  </InputWrapper>

  <InputWrapper
    v-if="miscEquipmentType"
    v-slot="{ ariaProps, inputClasses, inputId, invalid }"
    input-id="purchase-cargo-insurance-equipment-type-misc-name"
    :label="t('cargoInsurance.quote.equipmentTypeMisc')"
    :help-text="t('cargoInsurance.quote.equipmentTypeMiscHelpText')"
    class="col-12"
    :errors="v$.equipmentType.name.$errors"
  >
    <InputText
      :id="inputId"
      v-model="miscEquipmentType.name"
      v-bind="ariaProps"
      :class="inputClasses"
      :invalid="invalid"
      :placeholder="t('cargoInsurance.quote.equipmentTypeMisc')"
    />
  </InputWrapper>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useToast,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { type CargoInsuranceProviderEquipmentType, Shipment } from '@/model';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import InputText from 'primevue/inputtext';

const equipmentType = defineModel<CargoInsuranceProviderEquipmentType>();

const props = defineProps<{
  shipment: Shipment;
}>();

const { shipment } = toRefs(props);

const { t } = useI18n();

const toast = useToast();

const rules = {
  equipmentType: {
    required: required(t('cargoInsurance.commodity')),
    name: {
      required: required(t('cargoInsurance.quote.equipmentTypeMisc')),
    },
    id: {
      required: required(t('carriers.equipmentTypes')),
    },
  },
};
const validationObject = computed(() => ({
  equipmentType: equipmentType.value,
}));
const { v$ } = useValidations(validationObject, rules);

const availableEquipmentTypes: Ref<
  Loadable<CargoInsuranceProviderEquipmentType[]>
> = ref({ loading: false, data: [] });

const MISC_EQUIPMENT_TYPE_ID = 1;

const miscEquipmentType = computed(() => {
  //type inference needed since id can be number or string
  const isMiscEquipmentType =
    equipmentType.value?.id === MISC_EQUIPMENT_TYPE_ID;
  return isMiscEquipmentType ? equipmentType.value : undefined;
});

async function fetchEquipment(shipmentId: number) {
  availableEquipmentTypes.value.data = [];
  try {
    availableEquipmentTypes.value.loading = true;
    availableEquipmentTypes.value.data =
      await api.shipmentCargoInsurance.getEquipment(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading equipment', e);
    availableEquipmentTypes.value.error = e as Error;
    toast.addError({
      error: e,
      detail: t('errors.loadingParam', {
        param: t('carriers.equipmentTypes', 2),
      }),
    });
  } finally {
    availableEquipmentTypes.value.loading = false;
  }
}

function autoPopulateEquipmentType(
  shipmentEquipmentTypeNaturalId: string | undefined,
  cargoInsuranceProviderEquipmentTypes:
    CargoInsuranceProviderEquipmentType[] | undefined,
) {
  if (
    !cargoInsuranceProviderEquipmentTypes?.length ||
    !shipmentEquipmentTypeNaturalId
  ) {
    return;
  }

  let autoPopulatedEquipmentType:
    CargoInsuranceProviderEquipmentType | undefined;

  if (shipmentEquipmentTypeNaturalId === 'VAN') {
    autoPopulatedEquipmentType = cargoInsuranceProviderEquipmentTypes.find(
      (e) => e.name === 'Dry Van',
    );
  } else if (shipmentEquipmentTypeNaturalId === 'FLATBED') {
    autoPopulatedEquipmentType = cargoInsuranceProviderEquipmentTypes.find(
      (e) => e.name === 'Flatbed',
    );
  } else if (shipmentEquipmentTypeNaturalId === 'REFRIGERATED_TRAILER') {
    autoPopulatedEquipmentType = cargoInsuranceProviderEquipmentTypes.find(
      (e) => e.name === 'Reefer',
    );
  }

  if (autoPopulatedEquipmentType) {
    equipmentType.value = autoPopulatedEquipmentType;
    v$.value.$reset();
  }
}

watch(
  () => shipment.value.id,
  (shipmentId) => {
    if (shipmentId) {
      fetchEquipment(shipmentId);
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => shipment.value.equipmentType?.naturalId,
  () => {
    autoPopulateEquipmentType(
      props.shipment.equipmentType?.naturalId,
      availableEquipmentTypes.value.data,
    );
  },
  { immediate: true },
);

watch(
  () => availableEquipmentTypes.value.data?.length,
  () => {
    autoPopulateEquipmentType(
      shipment.value.equipmentType?.naturalId,
      availableEquipmentTypes.value.data,
    );
  },
);
</script>
